import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";

import { Radiobox, useRadiobox } from "../components/CheckRadiobox";
import {
  Button,
  ErrorMessage,
  Label,
  SplitLine,
  TextLink,
  TextMessage,
  Title,
  StyledButton
} from "../components/generic";
import {
  BorderBox,
  ContainerLogin,
  ContainerRedirect,
  HostStudentGrid,
  StyledBlock
} from "../components/containers";
import { InputLabel, InputLabelPassword, useInput } from "../components/Input";
import {
  EMPTY,
  HOST_PROFILE_EDIT_FIRST_ROUTE,
  HOST_PROFILE_VIEW_ROUTE,
  LOGIN_ROUTE,
  REGISTER_ROUTE,
  STUDENT_PROFILE_EDIT_FIRST_ROUTE,
  STUDENT_PROFILE_VIEW_ROUTE
} from "../utils/constants";
import { useValidateEmail, useValidateLength } from "../utils/validators";
import { useContextHospi } from "../context/ContextHospi";
import { theme } from "styles/theme";
import { LoaderCustom } from "components/Loader";
import { clearLocalStorage } from "utils/utilAmplify";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/intarfaces";

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { pathname: nameRoute, search } = useLocation();
  const { t, i18n } = useTranslation("login");
  const { setCurrentUserId, userProfile, isLoadingCurrentUser } =
    useContextHospi();
  // Input data
  const email = useInput(process.env.REACT_APP_AWS_USER_EMAIL as string);
  const password = useInput(process.env.REACT_APP_AWS_USER_PASS as string);
  const confirmPassword = useInput("");
  const code = useInput("");
  const [searchKey, setSearchKey] = useState<"student" | "host">("student");
  const studentORhost = useRadiobox("whoisit", searchKey);
  // Switchers states
  const [loginWrong, setLoginWrong] = useState(EMPTY);
  const [disabledSendButton, setDisabledSendButton] = useState(true);

  const [createdNewUser, setCreatedNewUser] = useState(false);
  const [createdNewPass, setCreatedNewPass] = useState(0);

  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassError, setShowPassError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allowRedirect, setAllowRedirect] = useState<boolean>(false);
  // const [isSignInInProgress, setIsSignInInProgress] = useState(false);
  // const [isSignUpInProgress, setIsSignUpInProgress] = useState(false);
  const seo = t("seo", { returnObjects: true }) as ISeo;
  const seoregister = t("seoregister", { returnObjects: true }) as ISeo;

  const isRegister =
    nameRoute === `/${i18n.language}${REGISTER_ROUTE.slice(5)}`;
  // Front-end validators
  const isEmail = useValidateEmail({ line: email.value });
  const isPass = useValidateLength({
    line: password.value,
    params: { min: 8, max: 20 }
  });

  useEffect(() => {
    search.slice(3) &&
      setSearchKey(search.slice(3) === "h" ? "host" : "student");
  }, [nameRoute]);

  useEffect(() => {
    searchKey && studentORhost.valueChange(searchKey);
  }, [searchKey]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDisabledSendButton(
      [
        isEmail.message === EMPTY,
        !isEmail.state,
        isPass.message === EMPTY,
        !isPass.state
      ].some((item) => item)
    );
  });

  useEffect(() => {
    if (isRegister) {
      if (password.value === confirmPassword.value) {
        setLoginWrong("");
      }
    }
    // eslint-disable-next-line
  }, [password.value, confirmPassword.value]);

  useEffect(() => {
    if (allowRedirect && userProfile?.user_type) {
      setLoading(false);
      if (userProfile?.is_profile_complete) {
        setAllowRedirect(false);
        navigate(
          userProfile.user_type === "student"
            ? `/${i18n.language}${STUDENT_PROFILE_VIEW_ROUTE.slice(5)}`
            : `/${i18n.language}${HOST_PROFILE_VIEW_ROUTE.slice(5)}`
        );
      }
      // If profile isn't complete
      else {
        setAllowRedirect(false);
        navigate(
          userProfile?.user_type === "student"
            ? `/${i18n.language}${STUDENT_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
            : `/${i18n.language}${HOST_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
        );
      }
      // eslint-disable-next-line
    }
  }, [userProfile, allowRedirect]);

  const handleLogin = async () => {
    if (loading || isLoadingCurrentUser) {
      return;
    }

    setLoading(true);
    clearLocalStorage();

    if (isRegister) {
      // setIsSignUpInProgress(true);
      if (password.value !== confirmPassword.value) {
        setLoginWrong(t("error.unequalPasswords"));
        // setIsSignUpInProgress(false);
        setLoading(false);
      } else {
        if (i18n.language === "index") {
          i18n.language = "gb";
        }
        Auth.signUp({
          username: String(email.value) as string,
          password: String(password.value) as string,
          attributes: {
            profile: studentORhost.selected as string,
            email: String(email.value),
            locale: i18n.language
          }
        })
          .then((data) => {
            setCreatedNewUser(true);
            // setIsSignUpInProgress(false);
            setLoading(false);
            window.dataLayer.push({
              event: "start_registration_" + studentORhost.selected,
              user_id: data.userSub,
              user_type: studentORhost.selected
            });
          })
          .catch((err) => {
            // setIsSignUpInProgress(false);
            // console.log(err.message);
            setLoginWrong(err.message);
            // setLoginWrong(t("error.emailExists"));
            setLoading(false);
          });
      }
    } else {
      // String().toLowerCase()
      // setIsSignInInProgress(true);
      Auth.signIn({
        username: email.value as string,
        password: password.value as string
      })
        .then((data) => {
          setCurrentUserId(data.attributes.sub);
          setAllowRedirect(true);
          window.dataLayer.push({
            event: "login",
            user_id: data.attributes.sub,
            user_type: data.attributes.profile
          });
          // setIsSignInInProgress(false);
          setLoading(false);
        })
        .catch((err) => {
          setCurrentUserId(undefined);
          // setIsSignInInProgress(false);
          setLoading(false);
          setLoginWrong(err.message as string);
          // if (err.message === "Incorrect username or password.") {
          //   setLoginWrong(t("error.userNotAuthorized"));
          // } else if (err.message === "User is not confirmed.") {
          //   setLoginWrong(t("error.userNotVerified"));
          // } else {

          // }
        });
    }
  };

  const runLink = (): void => {
    navigate(
      isRegister
        ? `/${i18n.language}${LOGIN_ROUTE.slice(5)}`
        : `/${i18n.language}${REGISTER_ROUTE.slice(5)}`
    );
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !disabledSendButton) {
      handleLogin();
    }
  };

  if (createdNewUser)
    return (
      <ContainerLogin>

        <ContainerRedirect>
          <Title m="0 0 24px 0">{t("message.title")}</Title>
          <Label lineHeight="150%" p="0">
            {t("message.text")}
          </Label>
        </ContainerRedirect>
        <Button
          main
          fontWeight="400"
          w="100%"
          m="24px 0 0 0"
          onClick={() => {
            setCreatedNewUser((prev) => !prev);
            navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
          }}
        >
          {t("message.buttonTitle")}
        </Button>
      </ContainerLogin>
    );

  // When changed password show info message
  if (createdNewPass === 2 || createdNewPass === 4)
    return (
      <ContainerLogin>
        <ContainerRedirect>
          <Title m="0 0 24px 0">
            {createdNewPass === 2
              ? t("message.titleForgotPass")
              : t("message.titleSuccessChangePass")}
          </Title>
          <Label lineHeight="150%" p="0">
            {createdNewPass === 2
              ? t("message.textForgotPass")
              : t("message.textChangedPass")}
          </Label>
        </ContainerRedirect>
        <Button
          main
          fontWeight="400"
          w="100%"
          m="24px 0 0 0"
          onClick={() => {
            createdNewPass === 4 &&
              navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
            setCreatedNewPass(createdNewPass === 4 ? 0 : 3);
          }}
        >
          {t("message.buttonTitleForgotPass")}
        </Button>
      </ContainerLogin>
    );

  // When changed password logics
  if (createdNewPass > 0) {
    const handleResetPass = () => {
      if (createdNewPass === 1) {
        Auth.forgotPassword(email.value as string)
          // .then((data) => {
          //   setCreatedNewPass(2);
          // })
          .catch((err) => {
            // console.log(err);
            setLoginWrong(err.message);
          });
      } else if (createdNewPass === 3) {
        if (password.value !== confirmPassword.value) {
          setLoginWrong(t("error.unequalPasswords"));
        } else {
          Auth.forgotPasswordSubmit(
            email.value as string,
            code.value as string,
            password.value as string
          )
            .then(() => {
              setCreatedNewPass(4);
              setLoginWrong(EMPTY);
            })
            .catch((err) => {
              // console.log(err);
              setLoginWrong(err.message);
            });
        }
      }
    };
    return (
      <ContainerLogin>
        <Title m="0 0 24px 0">
          {t(
            `login.${createdNewPass === 1 ? "titleReset" : "titleResetNewPass"}`
          )}
        </Title>
        {createdNewPass === 1 && (
          <>
            <TextMessage style={{ wordBreak: "break-word" }}>
              {t("login.textReset")}
            </TextMessage>
            <InputLabel
              {...email}
              title={t("login.email")}
              type="email"
              placeholder={t("login.placeholderemail")}
              p="24px 0 0 0"
              onBlur={() => {
                setShowEmailError(true);
              }}
              onFocus={() => {
                setShowEmailError(false);
              }}
              error={!isEmail.state && showEmailError}
            >
              <ErrorMessage error={!isEmail.state && showEmailError}>
                {t("error.wrongEmail")}
              </ErrorMessage>
            </InputLabel>
          </>
        )}
        {createdNewPass === 3 && (
          <>
            <TextMessage style={{ wordBreak: "break-word" }}>
              {t("login.textReset2")}
            </TextMessage>
            <InputLabel
              {...code}
              title={t("login.code.title")}
              type="text"
              placeholder={t("login.code.placeholder")}
              p="24px 0 24px 0"
            />
            <InputLabelPassword
              {...password}
              title={t("edit.newPassword")}
              type="password"
              placeholder="********"
              onBlur={() => {
                setShowPassError(true);
              }}
              onFocus={() => {
                setShowPassError(false);
              }}
              error={!isPass.state && showPassError}
            >
              <ErrorMessage error={!isPass.state && showPassError}>
                {t("error.wrongPassword")}
              </ErrorMessage>
            </InputLabelPassword>
            <InputLabelPassword
              {...confirmPassword}
              title={t("edit.confirmPassword")}
              type="password"
              placeholder="********"
              p="0 0 24px 0"
            />
          </>
        )}
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgBody : "red"}
          fontWeight="400"
        >
          {loginWrong}
        </Label>
        <Button
          main
          fontWeight="400"
          onClick={() => {
            handleResetPass();
            createdNewPass === 4 &&
              navigate(`/${i18n.language}${LOGIN_ROUTE.slice(5)}`);
            setCreatedNewPass(createdNewPass === 4 ? 0 : 3);
          }}
          w="100%"
          disabled={
            createdNewPass === 1
              ? !isEmail.state || !email.value
              : password.value !== confirmPassword.value ||
              String(code.value).length === 0
          }
          m="24px 0 24px 0"
        >
          {t(
            `login.${createdNewPass === 1
              ? "buttonSendPassToEmail"
              : "buttonCraetePass"
            }`
          )}
        </Button>
        <SplitLine>
          <hr />
          <label>{t("login.splitline")}</label>
        </SplitLine>
        <StyledButton
          // normal
          fontWeight="400"
          onClick={() => setCreatedNewPass(0)}
          w="100%"
          m="24px 0 0 0"
        >
          {t(`login.buttonFirst`)}
        </StyledButton>
      </ContainerLogin>
    );
  }

  return (
    <ContainerLogin>
      {seo && <HeaderSEO {...seo} />}
      {(loading || isLoadingCurrentUser) && (
        <LoaderCustom size={5} color={theme.colors.bgBody} />
      )}
      <Title m="0 0 24px 0">
        {t(`${isRegister ? "register" : "login"}.title`)}
      </Title>
      {isRegister && (
        <StyledBlock p="0 0 24px 0">
          {seoregister && <HeaderSEO {...seoregister} />}
          <Label p="0" fontWeight="600">
            {t("register.registerAs.title")}
          </Label>
          <HostStudentGrid>
            <BorderBox active={studentORhost.selected === "student"}>
              <Radiobox
                {...studentORhost}
                title={t("register.registerAs.student")}
                value="student"
              />
            </BorderBox>
            <BorderBox active={studentORhost.selected === "host"}>
              <Radiobox
                {...studentORhost}
                title={t("register.registerAs.host")}
                value="host"
              />
            </BorderBox>
          </HostStudentGrid>
        </StyledBlock>
      )}
      <form style={{ width: "100%" }}>
        <InputLabel
          {...email}
          title={t("login.email")}
          type="email"
          placeholder={t("login.placeholderemail")}
          onBlur={() => {
            setShowEmailError(true);
          }}
          onFocus={() => {
            setShowEmailError(false);
          }}
          error={!isEmail.state && showEmailError}
          onKeyPress={handleEnter}
        >
          <ErrorMessage error={!isEmail.state && showEmailError}>
            {t("error.wrongEmail")}
          </ErrorMessage>
        </InputLabel>
        <InputLabelPassword
          {...password}
          title={t("login.password")}
          type="password"
          placeholder="********"
          onBlur={() => {
            setShowPassError(true);
          }}
          onFocus={() => {
            setShowPassError(false);
          }}
          error={!isPass.state && showPassError}
          onKeyPress={handleEnter}
        >
          <ErrorMessage error={!isPass.state && showPassError}>
            {t("error.wrongPassword")}
          </ErrorMessage>
        </InputLabelPassword>
        {isRegister && (
          <InputLabelPassword
            {...confirmPassword}
            title={t("register.confirmPassword")}
            type="password"
            placeholder="********"
          />
        )}
        <Label
          p="0"
          fontColor={loginWrong === EMPTY ? theme.colors.bgBody : "red"}
          fontWeight="400"
        >
          {loginWrong}
        </Label>
        {!isRegister && (
          <TextLink onClick={() => setCreatedNewPass(1)}>
            {t("login.forgotPass")}
          </TextLink>
        )}
      </form>

      <StyledButton
        fontWeight="400"
        onClick={handleLogin}
        w="100%"
        m="24px 0 24px 0"
      >
        {t(`${isRegister ? "register" : "login"}.buttonFirst`)}
      </StyledButton>
      <SplitLine>
        <hr />
        <label>{t("login.splitline")}</label>
      </SplitLine>
      <StyledButton
        outline
        fontWeight="400"
        onClick={runLink}
        w="100%"
        m="24px 0 0 0"
      >
        {t(`${isRegister ? "register" : "login"}.buttonSecond`)}
      </StyledButton>
    </ContainerLogin>
  );
};
